import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import CaseLocationPicker from './CaseLocationPicker';
import AddressSuggestionDialog from './AddressSuggestionDialog';
import SaveCaseConfirmationDialog from './SaveCaseConfirmationDialog';
import { apiUrl } from '../utils/api';
import { TERRITORY_COUNTRIES, getTerritoryCountry, getTerritoryDivision, normalizeTerritoryName } from '../utils/territories';

const PROTOCOLO_GENERAL_REGEX = /^\d{2}-\d{1,4}$/;
const EMPTY_RURAL_ADDRESS = {
  nearbyReference: '',
  routeNumber: '',
  routeKilometer: '',
};

const cleanRuralAddressValue = (value = '') =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s;]+|[,\s;]+$/g, '')
    .trim();

const buildRuralAddressLabel = (draft = EMPTY_RURAL_ADDRESS) => {
  const nearbyReference = cleanRuralAddressValue(draft.nearbyReference);
  const routeNumber = cleanRuralAddressValue(draft.routeNumber);
  const routeKilometer = cleanRuralAddressValue(draft.routeKilometer);
  const parts = [];

  if (nearbyReference) {
    parts.push(`Cerca de ${nearbyReference}`);
  }

  if (routeNumber) {
    parts.push(`Ruta ${routeNumber}`);
  }

  if (routeKilometer) {
    parts.push(`km ${routeKilometer}`);
  }

  return parts.join(', ');
};

const hasRuralAddressData = (draft = EMPTY_RURAL_ADDRESS) =>
  Boolean(
    cleanRuralAddressValue(draft.nearbyReference) ||
      cleanRuralAddressValue(draft.routeNumber) ||
      cleanRuralAddressValue(draft.routeKilometer)
  );

const buildAcceptedSuggestedAddress = (suggestion = {}) => {
  const address = cleanRuralAddressValue(suggestion.address);
  const locality = cleanRuralAddressValue(suggestion.locality);

  if (!address) {
    return '';
  }

  if (!locality || address.toLocaleLowerCase().includes(locality.toLocaleLowerCase())) {
    return address;
  }

  return `${address}, ${locality}`;
};

const extractRuralAddressDraft = (address = '') => {
  const normalizedAddress = String(address || '').trim();
  if (!normalizedAddress) {
    return { ...EMPTY_RURAL_ADDRESS };
  }

  const nearbyReference = cleanRuralAddressValue(normalizedAddress.match(/cerca de\s+([^,;]+)/i)?.[1]);
  const routeNumber = cleanRuralAddressValue(
    normalizedAddress.match(/\bruta\s+([^,;]+?)(?=(?:\s*,\s*|\s+km\b|$))/i)?.[1]
  );
  const routeKilometer = cleanRuralAddressValue(normalizedAddress.match(/\bkm\.?\s*([^,;]+)/i)?.[1]);

  return {
    nearbyReference,
    routeNumber,
    routeKilometer,
  };
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const getUserDisplayName = (user = {}) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.username || user.email || 'Usuario sin nombre';
};


const ZOOTECHNICAL_GROUPS = [
  ['VACUNO', 'Bovino'], ['OVINO', 'Ovino'], ['CAPRINO', 'Caprino'], ['PORCINO', 'Porcino'], ['EQUINO', 'Equino'],
  ['CAMELIDOS_SUDAMERICANOS', 'Camélidos sudamericanos'], ['CANINO', 'Canino'], ['FELINO', 'Felino'],
  ['AVES_DE_CORRAL', 'Aves de corral'], ['ANIMALES_EXOTICOS', 'Animales exóticos'], ['ANIMALES_SILVESTRES', 'Animales silvestres'],
];
const FETAL_CATEGORIES = ['Feto primer tercio', 'Feto segundo tercio', 'Feto tercer tercio'];
const CATEGORIES_BY_GROUP = {
  VACUNO: [...FETAL_CATEGORIES, 'Ternero', 'Ternera', 'Novillito', 'Novillo', 'Vaquillona', 'Vaquillona de primer servicio', 'Vaca', 'Toro', 'Buey'],
  OVINO: [...FETAL_CATEGORIES, 'Cordero', 'Cordera', 'Borrego', 'Borrega', 'Oveja', 'Carnero', 'Capón'],
  CAPRINO: [...FETAL_CATEGORIES, 'Cabrito', 'Cabrita', 'Chivito', 'Chivita', 'Cabra', 'Chivo'],
  PORCINO: [...FETAL_CATEGORIES, 'Lechón lactante', 'Lechón destetado', 'Cachorra de reposición', 'Cerda en servicio', 'Cerda en gestación', 'Engorde', 'Padrillo'],
  EQUINO: [...FETAL_CATEGORIES, 'Potrillo', 'Potranca', 'Caballo', 'Yegua', 'Padrillo', 'Mula'],
  CAMELIDOS_SUDAMERICANOS: [...FETAL_CATEGORIES, 'Cría', 'Tui', 'Llama', 'Alpaca', 'Vicuña', 'Guanaco'],
  CANINO: [...FETAL_CATEGORIES, 'Cachorro', 'Joven', 'Adulto', 'Senior'],
  FELINO: [...FETAL_CATEGORIES, 'Cachorro', 'Joven', 'Adulto', 'Senior'],
  AVES_DE_CORRAL: ['Pollito', 'Pollo parrillero', 'Polla', 'Gallina', 'Gallo', 'Pavo', 'Pato', 'Codorniz'],
  ANIMALES_EXOTICOS: ['Cría', 'Juvenil', 'Adulto'], ANIMALES_SILVESTRES: ['Cría', 'Juvenil', 'Adulto'],
};
const emptyAnimalRow = (tipo) => ({ tipo, cantidad: '', edad_valor: '', edad_unidad: '', sexo: '', grupo_zootecnico: '', categoria: '' });
const parseLocalizedDecimal = (value) => {
  if (typeof value === 'number') return value;
  const normalized = String(value ?? '').trim().replace(',', '.');
  return normalized === '' ? null : Number(normalized);
};
const isValidAge = (value) => {
  const parsed = parseLocalizedDecimal(value);
  return parsed !== null && Number.isFinite(parsed) && parsed >= 0;
};
const AnimalSituationSection = ({ title, tipo, rows, errors, onAdd, onChange, onRemove }) => (
  <div className="border rounded-3 p-3 mb-3">
    <div className="d-flex justify-content-between align-items-center gap-2 mb-2"><h5 className="mb-0">{title}</h5><button type="button" className="btn btn-outline-brand btn-sm" onClick={() => onAdd(tipo)}>+ Agregar grupo</button></div>
    {rows.map((row, index) => <div className="row g-2 align-items-end border-top pt-3 mt-3" key={row.clientId}>
      <div className="col-6 col-md-2"><label className="form-label">Cantidad</label><input type="number" min="1" step="1" className="form-control" value={row.cantidad} onChange={(e) => onChange(tipo,row.clientId,'cantidad',e.target.value)} /></div>
      <div className="col-6 col-md-2"><label className="form-label">Edad</label><input type="text" inputMode="decimal" className="form-control" value={row.edad_valor} onChange={(e) => onChange(tipo,row.clientId,'edad_valor',e.target.value)} placeholder="Ej. 1,5" /></div>
      <div className="col-6 col-md-2"><label className="form-label">Unidad</label><select className="form-select" value={row.edad_unidad} onChange={(e) => onChange(tipo,row.clientId,'edad_unidad',e.target.value)}><option value="">Seleccionar</option><option value="ANIOS">Años</option><option value="MESES">Meses</option><option value="DIAS">Días</option></select></div>
      <div className="col-6 col-md-2"><label className="form-label">Sexo</label><select className="form-select" value={row.sexo} onChange={(e) => onChange(tipo,row.clientId,'sexo',e.target.value)}><option value="">Sin especificar</option><option value="MACHO">Macho</option><option value="HEMBRA">Hembra</option></select></div>
      <div className="col-12 col-md-2"><label className="form-label">Grupo zootécnico</label><select className="form-select" value={row.grupo_zootecnico} onChange={(e) => onChange(tipo,row.clientId,'grupo_zootecnico',e.target.value)}><option value="">Seleccionar</option>{ZOOTECHNICAL_GROUPS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
      <div className="col-10 col-md-2"><label className="form-label">Categoría</label><select className="form-select" value={row.categoria} disabled={!row.grupo_zootecnico} onChange={(e) => onChange(tipo,row.clientId,'categoria',e.target.value)}><option value="">Seleccionar</option>{(CATEGORIES_BY_GROUP[row.grupo_zootecnico] || []).map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
      <div className="col-2 col-md-12 text-end"><button type="button" className="btn btn-link text-danger p-0" onClick={() => onRemove(tipo,row.clientId)} aria-label={`Quitar fila ${index+1}`}>Quitar</button></div>
      {errors[row.clientId] ? <div className="col-12 text-danger small">{errors[row.clientId]}</div> : null}
    </div>)}
    {!rows.length ? <div className="form-text">No hay grupos cargados.</div> : null}
  </div>
);

const EMPTY_FORM_DATA = {
  protocolo_general: '',
  fecha_caso: "",
  tipo_estudio: "",
  cantidad_animales_totales: "",
  edad_totales_valor: "",
  edad_totales_unidad: "",
  diagnostico_primario: "",
  diagnostico_secundario: "",
  propietario: "",
  pais: "",
  provincia: "",
  localidad: "",
  direccion: "",
  latitud: "",
  longitud: "",
  telefono: "",
  email: "",
  antecedentes_anamnesis: "",
  observaciones: "",
  remitente: "",
  medico_responsable: "",
};

const normalizeCaseData = (caseData = {}) => ({
  protocolo_general: caseData.protocolo_general || '',
  fecha_caso: caseData.fecha_caso || "",
  tipo_estudio: caseData.tipo_estudio || "",
  cantidad_animales_totales: caseData.cantidad_animales_totales ?? "",
  edad_totales_valor: caseData.edad_totales_valor ?? "",
  edad_totales_unidad: caseData.edad_totales_unidad || "",
  diagnostico_primario: caseData.diagnostico_primario || "",
  diagnostico_secundario: caseData.diagnostico_secundario || "",
  propietario: caseData.propietario || "",
  pais: caseData.pais || "",
  provincia: caseData.provincia || "",
  localidad: caseData.localidad || "",
  direccion: caseData.direccion || "",
  latitud: caseData.latitud === null || caseData.latitud === undefined ? "" : String(caseData.latitud),
  longitud: caseData.longitud === null || caseData.longitud === undefined ? "" : String(caseData.longitud),
  telefono: caseData.telefono || "",
  email: caseData.email || "",
  antecedentes_anamnesis: caseData.antecedentes_anamnesis || "",
  observaciones: caseData.observaciones || "",
  remitente: caseData.remitente || "",
  medico_responsable: caseData.medico_responsable || "",
});

const NuevoCaso = ({ caseId = null }) => {
  const isEditMode = Boolean(caseId);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [pendingAddressSuggestion, setPendingAddressSuggestion] = useState(null);
  const [ruralAddressDraft, setRuralAddressDraft] = useState(EMPTY_RURAL_ADDRESS);
  const [showRuralAddressHelper, setShowRuralAddressHelper] = useState(false);
  const [manualProtocolEnabled, setManualProtocolEnabled] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitNotice, setSubmitNotice] = useState(null);
  const [loadingCase, setLoadingCase] = useState(isEditMode);
  const [usuarios, setUsuarios] = useState([]);
  const [animalRows, setAnimalRows] = useState({ MUERTO: [], ENFERMO: [] });
  const [animalRowErrors, setAnimalRowErrors] = useState({});
  const [createdCaseSummary, setCreatedCaseSummary] = useState(null);
  const [showCreatedCaseSummary, setShowCreatedCaseSummary] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const navigate = useNavigate();
  const selectedCountry = getTerritoryCountry(formData.pais);
  const selectedDivision = getTerritoryDivision(selectedCountry, formData.provincia);
  const medicoExistsInOptions = usuarios.some((user) => user.username === formData.medico_responsable);
  const medicoLegacyLabel = formData.medico_responsable
    ? `Valor actual: ${formData.medico_responsable}`
    : '';

  useEffect(() => {
    if (!isEditMode) {
      setFormData(EMPTY_FORM_DATA);
      setAnimalRows({ MUERTO: [], ENFERMO: [] });
      setPendingAddressSuggestion(null);
      setRuralAddressDraft({ ...EMPTY_RURAL_ADDRESS });
      setShowRuralAddressHelper(false);
      setManualProtocolEnabled(false);
      setLoadingCase(false);
      return;
    }

    let active = true;
    setLoadingCase(true);
    setSubmitNotice(null);

    axios
      .get(apiUrl(`/api/ingresos_casos/${caseId}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        const normalizedCaseData = normalizeCaseData(response.data);
        const nextRuralDraft = extractRuralAddressDraft(normalizedCaseData.direccion);
        setFormData(normalizedCaseData);
        const savedRows = response.data.animales_situacion || [];
        setAnimalRows({
          MUERTO: savedRows.filter((row) => row.tipo === 'MUERTO').map((row) => ({ ...row, clientId: `saved-${row.id}` })),
          ENFERMO: savedRows.filter((row) => row.tipo === 'ENFERMO').map((row) => ({ ...row, clientId: `saved-${row.id}` })),
        });
        setPendingAddressSuggestion(null);
        setRuralAddressDraft(nextRuralDraft);
        setShowRuralAddressHelper(hasRuralAddressData(nextRuralDraft));
        setManualProtocolEnabled(false);
      })
      .catch((error) => {
        console.error("Error al cargar el caso:", error);
        if (!active) {
          return;
        }

        setSubmitNotice({
          type: 'error',
          text: 'No se pudo cargar el caso para editar.',
        });
        toast.error('No se pudo cargar el caso para editar.');
      })
      .finally(() => {
        if (active) {
          setLoadingCase(false);
        }
      });

    return () => {
      active = false;
    };
  }, [caseId, isEditMode]);

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/usuarios/opciones/?solo_medicos=1'), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setUsuarios(response.data || []);
      })
      .catch((error) => {
        console.error('Error al cargar usuarios:', error);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: nextValue,
      ...(name === 'pais' && normalizeTerritoryName(getTerritoryCountry(value)?.name || value) !== normalizeTerritoryName(getTerritoryCountry(prevData.pais)?.name || prevData.pais)
        ? { provincia: '', localidad: '', direccion: '', latitud: '', longitud: '' } : {}),
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,

      [name]: '',
    }));

    if (name === 'direccion' || name === 'pais') {
      setPendingAddressSuggestion(null);
    }
    if (name === 'pais') {
      setRuralAddressDraft({ ...EMPTY_RURAL_ADDRESS });
      setShowRuralAddressHelper(false);
    }
  };

  const addAnimalRow = (tipo) => setAnimalRows((current) => ({ ...current, [tipo]: [...current[tipo], { ...emptyAnimalRow(tipo), clientId: `${tipo}-${Date.now()}-${Math.random()}` }] }));
  const removeAnimalRow = (tipo, clientId) => setAnimalRows((current) => ({ ...current, [tipo]: current[tipo].filter((row) => row.clientId !== clientId) }));
  const changeAnimalRow = (tipo, clientId, field, value) => {
    setAnimalRows((current) => ({ ...current, [tipo]: current[tipo].map((row) => row.clientId === clientId ? { ...row, [field]: value, ...(field === 'grupo_zootecnico' ? { categoria: '' } : {}) } : row) }));
    setAnimalRowErrors((current) => ({ ...current, [clientId]: '' }));
  };

  const validateForm = () => {
    const errors = {};

    if (manualProtocolEnabled && !formData.protocolo_general.trim()) {
      errors.protocolo_general = 'Debes completar el protocolo general si activas la edición manual.';
    } else if (manualProtocolEnabled && !PROTOCOLO_GENERAL_REGEX.test(formData.protocolo_general.trim())) {
      errors.protocolo_general = 'El protocolo general debe tener el formato NN-NNNN: dos números, guion y hasta 4 números.';
    }

    const rowErrors = {};
    Object.values(animalRows).flat().forEach((row) => {
      if (row.cantidad === '' || Number(row.cantidad) < 1) rowErrors[row.clientId] = 'La cantidad debe ser al menos 1.';
      else if ((row.edad_valor !== '') !== Boolean(row.edad_unidad)) rowErrors[row.clientId] = 'Completa el valor y la unidad de la edad.';
      else if (row.edad_valor !== '' && !isValidAge(row.edad_valor)) rowErrors[row.clientId] = 'Ingresa una edad válida (por ejemplo, 1,5).';
      else if (row.edad_unidad === 'MESES' && parseLocalizedDecimal(row.edad_valor) > 11) rowErrors[row.clientId] = 'Para un año o más, utiliza años.';
      else if (row.edad_unidad === 'DIAS' && parseLocalizedDecimal(row.edad_valor) > 30) rowErrors[row.clientId] = 'Para un mes o más, utiliza meses o años.';
    });
    if ((formData.edad_totales_valor !== '') !== Boolean(formData.edad_totales_unidad)) errors.edad_totales_valor = 'Completa el valor y la unidad de la edad total.';
    if (formData.edad_totales_valor !== '' && !isValidAge(formData.edad_totales_valor)) errors.edad_totales_valor = 'Ingresa una edad válida (por ejemplo, 1,5).';
    if (formData.edad_totales_unidad === 'MESES' && parseLocalizedDecimal(formData.edad_totales_valor) > 11) errors.edad_totales_valor = 'Para un año o más, utiliza años.';
    if (formData.edad_totales_unidad === 'DIAS' && parseLocalizedDecimal(formData.edad_totales_valor) > 30) errors.edad_totales_valor = 'Para un mes o más, utiliza meses o años.';
    const total = formData.cantidad_animales_totales === '' ? null : Number(formData.cantidad_animales_totales);
    ['MUERTO', 'ENFERMO'].forEach((tipo) => {
      const subtotal = animalRows[tipo].reduce((sum, row) => sum + Number(row.cantidad || 0), 0);
      if (total !== null && subtotal > total) errors.animales_situacion = `La suma de animales ${tipo === 'MUERTO' ? 'muertos' : 'enfermos'} no puede superar el total.`;
    });
    const affectedTotal = Object.values(animalRows).flat().reduce((sum, row) => sum + Number(row.cantidad || 0), 0);
    if (total !== null && affectedTotal > total) errors.animales_situacion = 'La suma de enfermos y muertos no puede superar el total de animales.';
    setAnimalRowErrors(rowErrors);
    if (Object.keys(rowErrors).length) errors.animales_situacion = 'Revisa los grupos de animales.';

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (savingRef.current || createdCaseSummary) return;
    setSubmitNotice(null);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmitNotice({
        type: 'error',
        text: Object.values(errors).join(' '),
      });
      toast.error('Revisa los campos del caso.');
      return;
    }

    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!showSaveConfirmation || savingRef.current || createdCaseSummary) return;
    savingRef.current = true;
    setIsSaving(true);

    try {
      const requestConfig = {
        headers: getAuthHeaders(),
      };
      const payload = {
        ...formData,
        cantidad_animales_totales: formData.cantidad_animales_totales === '' ? null : Number(formData.cantidad_animales_totales),
        edad_totales_valor: formData.edad_totales_valor === '' ? null : parseLocalizedDecimal(formData.edad_totales_valor),
        edad_totales_unidad: formData.edad_totales_unidad || null,
        animales_situacion: Object.values(animalRows).flat().map(({ clientId, id, ...row }) => ({
          ...row, cantidad: Number(row.cantidad), edad_valor: row.edad_valor === '' ? null : parseLocalizedDecimal(row.edad_valor),
          edad_unidad: row.edad_unidad || null, sexo: row.sexo || null,
          grupo_zootecnico: row.grupo_zootecnico || null, categoria: row.categoria || null,
        })),
      };

      if (isEditMode) {
        const response = await axios.put(apiUrl(`/api/ingresos_casos/${caseId}/`), payload, requestConfig);
        const normalizedCaseData = normalizeCaseData(response.data);
        const nextRuralDraft = extractRuralAddressDraft(normalizedCaseData.direccion);
        setFormData(normalizedCaseData);
        setPendingAddressSuggestion(null);
        setRuralAddressDraft(nextRuralDraft);
        setShowRuralAddressHelper(hasRuralAddressData(nextRuralDraft));
        toast.success("Caso clínico actualizado con éxito.");
        setSubmitNotice({
          type: 'success',
          text: 'Caso clínico actualizado con éxito. Puedes seguir completando la información o volver al listado cuando quieras.',
        });
        return;
      }

      const response = await axios.post(apiUrl('/api/ingresos_casos/'), payload, requestConfig);
      const protocoloGeneral = response.data.protocolo_general;

      setFormData((prevData) => ({
        ...prevData,
        protocolo_general: protocoloGeneral,
      }));
      setPendingAddressSuggestion(null);
      setRuralAddressDraft({ ...EMPTY_RURAL_ADDRESS });
      setShowRuralAddressHelper(false);
      setCreatedCaseSummary({
        protocoloGeneral,
        fechaCaso: response.data.fecha_caso || formData.fecha_caso || 'Sin especificar',
        propietario: response.data.propietario || formData.propietario || 'Sin especificar',
        tipoEstudio: response.data.tipo_estudio_nombre || response.data.tipo_estudio || formData.tipo_estudio || 'Sin especificar',
        ubicacion: [response.data.localidad || formData.localidad, response.data.provincia || formData.provincia, response.data.pais || formData.pais].filter(Boolean).join(', ') || 'Sin especificar',
        animalesTotales: response.data.cantidad_animales_totales ?? payload.cantidad_animales_totales ?? 'Sin especificar',
        animalesEnfermos: response.data.cantidad_animales_enfermos ?? payload.animales_situacion.filter((row) => row.tipo === 'ENFERMO').reduce((sum, row) => sum + row.cantidad, 0),
        animalesMuertos: response.data.cantidad_animales_muertos ?? payload.animales_situacion.filter((row) => row.tipo === 'MUERTO').reduce((sum, row) => sum + row.cantidad, 0),
        isPackToro: formData.tipo_estudio === 'PACK_TORO',
      });
      setShowCreatedCaseSummary(true);
    } catch (error) {
      console.error(`Error al ${isEditMode ? 'actualizar' : 'crear'} el caso:`, error);
      if (error.response && error.response.data) {
        console.error("Detalles del error:", error.response.data);
        const backendMessage = Object.values(error.response.data || {})
          .flat()
          .join(' ');
        setSubmitNotice({
          type: 'error',
          text: backendMessage || `No se pudo ${isEditMode ? 'actualizar' : 'crear'} el caso clínico.`,
        });
        toast.error("Error: " + JSON.stringify(error.response.data));
      } else {
        setSubmitNotice({
          type: 'error',
          text: `Error desconocido al ${isEditMode ? 'actualizar' : 'crear'} el caso clínico.`,
        });
        toast.error(`Error desconocido al ${isEditMode ? 'actualizar' : 'crear'} el caso clínico`);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
      setShowSaveConfirmation(false);
    }
  };

  const continueAfterCaseCreation = () => {
    if (!createdCaseSummary) return;
    const { protocoloGeneral } = createdCaseSummary;
    navigate('/ingreso-muestra', { state: { protocoloGeneral } });
  };

  const handleLocationChange = useCallback(({ lat, lng, resolvedLocation }) => {
    const suggestedAddress = String(resolvedLocation?.address || '').trim();
    const currentAddress = String(formData.direccion || '').trim();

    setFormData((prevData) => ({
      ...prevData,
      latitud: lat === '' ? '' : String(lat),
      longitud: lng === '' ? '' : String(lng),
      pais: resolvedLocation?.country || prevData.pais,
      provincia: resolvedLocation?.province || prevData.provincia,
      localidad: resolvedLocation?.locality || prevData.localidad,
    }));

    if (suggestedAddress && suggestedAddress !== currentAddress) {
      setPendingAddressSuggestion({
        address: suggestedAddress,
        country: resolvedLocation?.country || '',
        locality: resolvedLocation?.locality || '',
        province: resolvedLocation?.province || '',
      });
      return;
    }

    if (suggestedAddress === currentAddress || (lat === '' && lng === '')) {
      setPendingAddressSuggestion(null);
    }
  }, [formData.direccion]);

  const handleAcceptSuggestedAddress = () => {
    if (!pendingAddressSuggestion?.address) {
      return;
    }

    const acceptedAddress = buildAcceptedSuggestedAddress(pendingAddressSuggestion);
    const nextRuralDraft = extractRuralAddressDraft(acceptedAddress);
    setFormData((prevData) => ({
      ...prevData,
      direccion: acceptedAddress,
      pais: pendingAddressSuggestion.country || prevData.pais,
      localidad: pendingAddressSuggestion.locality || prevData.localidad,
      provincia: pendingAddressSuggestion.province || prevData.provincia,
    }));
    setRuralAddressDraft(nextRuralDraft);
    setShowRuralAddressHelper(hasRuralAddressData(nextRuralDraft));
    setPendingAddressSuggestion(null);
  };

  const handleRuralAddressDraftChange = (field, value) => {
    setRuralAddressDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleApplyRuralAddress = () => {
    const ruralAddressLabel = buildRuralAddressLabel(ruralAddressDraft);
    if (!ruralAddressLabel) {
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      direccion: ruralAddressLabel,
    }));
    setShowRuralAddressHelper(true);
    setPendingAddressSuggestion(null);
  };

  const handleClearRuralAddressDraft = () => {
    setRuralAddressDraft({ ...EMPTY_RURAL_ADDRESS });
  };

  const ruralAddressPreview = buildRuralAddressLabel(ruralAddressDraft);
  const ruralAddressHasData = hasRuralAddressData(ruralAddressDraft);
  const ruralAddressMatchesCurrentValue =
    ruralAddressPreview && ruralAddressPreview === String(formData.direccion || '').trim();

  const handleManualProtocolToggle = (event) => {
    const enabled = event.target.checked;
    setManualProtocolEnabled(enabled);
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      protocolo_general: '',
    }));

    if (!enabled) {
      setFormData((prevData) => ({
        ...prevData,
        protocolo_general: '',
      }));
    }
  };

  if (loadingCase) {
    return (
      <div className="surface-card">
        <div className="dashboard-empty dashboard-empty--compact">
          <div className="d-flex align-items-center justify-content-center gap-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
            <span>Cargando caso para editar...</span>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="surface-card">
      <form onSubmit={handleSubmit}>
        <div className="form-highlight mb-4">
          <div>
            <span className="section-kicker">{isEditMode ? 'Edición' : 'Paso 1'}</span>
            <h3 className="form-title">{isEditMode ? 'Editar caso clínico' : 'Datos principales del caso'}</h3>
          </div>
          {formData.protocolo_general && (
            <div className="protocol-chip">{formData.protocolo_general}</div>
          )}
        </div>


        {submitNotice ? (
          <div className={`alert ${submitNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
            {submitNotice.text}
          </div>
        ) : null}

        <div className="mb-4">
          <label className="form-label">Protocolo general</label>
          <div className="form-check mb-3">
            <input
              id="manualProtocolEnabled"
              type="checkbox"
              className="form-check-input"
              checked={manualProtocolEnabled}
              onChange={handleManualProtocolToggle}
              disabled={isEditMode}
            />
            <label className="form-check-label" htmlFor="manualProtocolEnabled">
              {isEditMode ? 'Protocolo bloqueado en edición' : 'Editar protocolo manualmente'}
            </label>
          </div>

          {isEditMode ? (
            <div className="form-text mb-3">
              El protocolo general identifica al caso y no se modifica desde esta pantalla.
            </div>
          ) : manualProtocolEnabled ? (
            <div className="alert subtle-alert subtle-alert--info mb-3" role="note">
              Usa esta opción solo si necesitas cargar un protocolo existente o especial. Si el valor ya existe o no sigue el formato esperado, el backend puede rechazar el caso.
            </div>
          ) : (
            <div className="form-text mb-3">
              El sistema generará automáticamente el protocolo general al guardar el caso.
            </div>
          )}

          <input
            type="text"
            className={`form-control ${(isEditMode || !manualProtocolEnabled) ? 'bg-light' : ''} ${validationErrors.protocolo_general ? 'is-invalid' : ''}`}
            name="protocolo_general"
            value={formData.protocolo_general}
            onChange={handleChange}
            readOnly={isEditMode || !manualProtocolEnabled}
            pattern="\d{2}-\d{1,4}"
            inputMode="numeric"
            placeholder={isEditMode ? 'Protocolo del caso' : manualProtocolEnabled ? 'Ej. 26-032 o 26-2154' : 'Se genera automáticamente al guardar'}
          />
          {validationErrors.protocolo_general ? <div className="invalid-feedback">{validationErrors.protocolo_general}</div> : null}
          {manualProtocolEnabled && !validationErrors.protocolo_general ? (
            <div className="form-text mt-2">
              Formato permitido: <strong>26-032</strong> o <strong>26-2154</strong>.
            </div>
          ) : null}
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Fecha del caso</label>
            <input
              type="date"
              className={`form-control ${validationErrors.fecha_caso ? 'is-invalid' : ''}`}
              name="fecha_caso"
              value={formData.fecha_caso}
              onChange={handleChange}
            />
            {validationErrors.fecha_caso ? <div className="invalid-feedback">{validationErrors.fecha_caso}</div> : null}
            <div className="form-text">
              Usa este campo para casos viejos o retrospectivos. Es independiente de la fecha de ingreso al sistema.
            </div>
          </div>
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Tipo de estudio</label>
            <select
              className="form-select"
              name="tipo_estudio"
              value={formData.tipo_estudio}
              onChange={handleChange}
            >
              <option value="">Seleccionar</option>
              <option value="CASO_CLINICO">Caso clínico</option>
              <option value="ESTUDIO_SIMPLE">Estudio simple</option>
              <option value="ENSAYO">Ensayo</option>
              <option value="PACK_TORO">Pack toro</option>
            </select>
          </div>
          <div className="col-md-6 mt-3">
            <label className="form-label">Propietario</label>
            <input
              type="text"
              className={`form-control ${validationErrors.propietario ? 'is-invalid' : ''}`}
              name="propietario"
              value={formData.propietario}
              onChange={handleChange}
            />
            {validationErrors.propietario ? <div className="invalid-feedback">{validationErrors.propietario}</div> : null}
          </div>
          <div className="col-md-3 mt-3">
            <label className="form-label" htmlFor="caso-pais">País</label>
            <input
              id="caso-pais"
              type="text"
              className={`form-control ${validationErrors.pais ? 'is-invalid' : ''}`}
              name="pais"
              list="caso-paises"
              value={formData.pais}
              onChange={handleChange}
              maxLength={100}
              placeholder="Ej. Argentina"
            />
            <datalist id="caso-paises">
              {TERRITORY_COUNTRIES.map((country) => <option key={country.code} value={country.name} />)}
            </datalist>
            {validationErrors.pais ? <div className="invalid-feedback">{validationErrors.pais}</div> : null}
          </div>
          <div className="col-md-3 mt-3">
            <label className="form-label" htmlFor="caso-provincia">{selectedCountry?.divisionLabel || 'Provincia / Estado / Región'}</label>
            {selectedCountry ? <select
              id="caso-provincia"
              className="form-select"
              name="provincia"
              value={selectedDivision?.name || formData.provincia}
              onChange={handleChange}
            >
              <option value="">Seleccionar</option>
              {formData.provincia && !selectedDivision ? <option value={formData.provincia}>{formData.provincia} (valor actual)</option> : null}
              {selectedCountry.divisions.map((division) => <option key={division.code} value={division.name}>{division.name}</option>)}
            </select> : <input
              id="caso-provincia"
              type="text"
              className={`form-control ${validationErrors.provincia ? 'is-invalid' : ''}`}
              name="provincia"
              value={formData.provincia}
              onChange={handleChange}
            />}
            {validationErrors.provincia ? <div className="invalid-feedback">{validationErrors.provincia}</div> : null}
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Localidad</label>
            <input type="text" className="form-control" name="localidad" value={formData.localidad} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="caso-direccion">Dirección</label>
            <div className="case-address-field">
              <input id="caso-direccion" type="text" className="form-control" name="direccion" value={formData.direccion} onChange={handleChange} />
              <button
                type="button"
                className="btn btn-outline-brand btn-sm case-address-field__button"
                onClick={() => setShowRuralAddressHelper((current) => !current)}
                aria-expanded={showRuralAddressHelper}
              >
                {showRuralAddressHelper ? 'Ocultar rural' : 'Dirección rural'}
              </button>
            </div>

            <div className="mt-3">
              <div className="form-text mt-2">
                Para campos, fincas o puestos, puedes armar la dirección con una referencia cercana, la ruta y el km utilizando el botón de Dirección rural.
              </div>

              {!showRuralAddressHelper && ruralAddressHasData ? (
                <div className="form-text mt-2">
                  Guardado en la ayuda rural: <strong>{ruralAddressPreview}</strong>
                </div>
              ) : null}

              {showRuralAddressHelper ? (
                <div className="alert subtle-alert subtle-alert--info mt-3 mb-0" role="note">
                  <strong>Referencia rural</strong>

                  <div className="row g-3 mt-1">
                    <div className="col-12">
                      <label className="form-label">Cerca de</label>
                      <input
                        type="text"
                        className="form-control"
                        value={ruralAddressDraft.nearbyReference}
                        onChange={(event) => handleRuralAddressDraftChange('nearbyReference', event.target.value)}
                        placeholder="Ej. Finca La Paz o paraje cercano"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Ruta</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        value={ruralAddressDraft.routeNumber}
                        onChange={(event) => handleRuralAddressDraftChange('routeNumber', event.target.value)}
                        placeholder="Ej. 68"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Km</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={ruralAddressDraft.routeKilometer}
                        onChange={(event) => handleRuralAddressDraftChange('routeKilometer', event.target.value)}
                        placeholder="Ej. 12.5"
                      />
                    </div>
                  </div>

                  {ruralAddressPreview ? (
                    <>
                      <div className="form-text mt-3">Vista previa</div>
                      <div className="readonly-field mt-2">
                        <strong className="readonly-field__value">{ruralAddressPreview}</strong>
                      </div>
                    </>
                  ) : null}

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-brand btn-sm"
                      onClick={handleApplyRuralAddress}
                      disabled={!ruralAddressPreview}
                    >
                      {ruralAddressMatchesCurrentValue ? 'Dirección rural aplicada' : 'Usar formato rural en dirección'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={handleClearRuralAddressDraft}
                      disabled={!ruralAddressPreview}
                    >
                      Limpiar ayuda rural
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <CaseLocationPicker
            latitude={formData.latitud}
            longitude={formData.longitud}
            locality={formData.localidad}
            province={formData.provincia}
            country={formData.pais}
            onChange={handleLocationChange}
          />
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Latitud</label>
            <input
              type="number"
              step="0.000001"
              className="form-control"
              name="latitud"
              value={formData.latitud}
              onChange={handleChange}
              placeholder="-34.603722"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Longitud</label>
            <input
              type="number"
              step="0.000001"
              className="form-control"
              name="longitud"
              value={formData.longitud}
              onChange={handleChange}
              placeholder="-58.381592"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Teléfono</label>
            <input type="text" className="form-control" name="telefono" value={formData.telefono} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Remitente</label>
          <input type="text" className="form-control" name="remitente" value={formData.remitente} onChange={handleChange} />
        </div>

        <div className="mb-3">
          <label className="form-label">Médico responsable</label>
          <select
            name="medico_responsable"
            className={`form-select ${validationErrors.medico_responsable ? 'is-invalid' : ''}`}
            value={formData.medico_responsable}
            onChange={handleChange}
          >
            <option value="">Seleccionar</option>
            {formData.medico_responsable && !medicoExistsInOptions ? (
              <option value={formData.medico_responsable}>
                {medicoLegacyLabel}
              </option>
            ) : null}
            {usuarios.map((user) => (
              <option key={user.id} value={user.username}>
                {getUserDisplayName(user)}
              </option>
            ))}
          </select>
          {validationErrors.medico_responsable ? (
            <div className="invalid-feedback">{validationErrors.medico_responsable}</div>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="form-label">Antecedentes / Anamnesis</label>
          <textarea
            className="form-control"
            name="antecedentes_anamnesis"
            value={formData.antecedentes_anamnesis}
            onChange={handleChange}
            rows="4"
            placeholder="Resumen clínico, antecedentes relevantes y anamnesis del caso"
          />
        </div>

        <div className="mb-4">
          <h4 className="form-title mb-1">Situación de los animales</h4>
          <div className="form-text mb-3">Puedes agregar varios grupos con distinta edad, sexo y categoría.</div>
          <AnimalSituationSection title="Animales muertos" tipo="MUERTO" rows={animalRows.MUERTO} errors={animalRowErrors} onAdd={addAnimalRow} onChange={changeAnimalRow} onRemove={removeAnimalRow} />
          <AnimalSituationSection title="Animales enfermos" tipo="ENFERMO" rows={animalRows.ENFERMO} errors={animalRowErrors} onAdd={addAnimalRow} onChange={changeAnimalRow} onRemove={removeAnimalRow} />
          <div className="border rounded-3 p-3">
            <h5>Animales totales</h5><div className="row g-2">
              <div className="col-md-4"><label className="form-label">Cantidad total</label><input type="number" min="0" step="1" name="cantidad_animales_totales" className="form-control" value={formData.cantidad_animales_totales} onChange={handleChange} /></div>
              <div className="col-md-4"><label className="form-label">Edad de referencia</label><input type="text" inputMode="decimal" name="edad_totales_valor" className="form-control" value={formData.edad_totales_valor} onChange={handleChange} placeholder="Ej. 1,5" /></div>
              <div className="col-md-4"><label className="form-label">Unidad</label><select name="edad_totales_unidad" className="form-select" value={formData.edad_totales_unidad} onChange={handleChange}><option value="">Seleccionar</option><option value="ANIOS">Años</option><option value="MESES">Meses</option><option value="DIAS">Días</option></select></div>
            </div>
          </div>
          {validationErrors.edad_totales_valor ? <div className="text-danger small mt-2">{validationErrors.edad_totales_valor}</div> : null}
          {validationErrors.animales_situacion ? <div className="text-danger small mt-2">{validationErrors.animales_situacion}</div> : null}
        </div>

        <div className="d-flex justify-content-end gap-2 pt-2">
          {createdCaseSummary ? (
            <button type="button" className="btn btn-outline-brand" onClick={() => setShowCreatedCaseSummary(true)}>
              Ver caso guardado
            </button>
          ) : null}
          {isEditMode ? (
            <button
              type="button"
              className="btn btn-outline-brand"
              onClick={() => navigate('/ver-casos')}
            >
              Volver al listado
            </button>
          ) : null}
          <button type="submit" className="btn btn-brand" disabled={isSaving || Boolean(createdCaseSummary)}>
            {isSaving ? 'Guardando…' : isEditMode ? 'Guardar cambios' : 'Guardar caso'}
          </button>
        </div>
      </form>

      {showSaveConfirmation ? (
        <SaveCaseConfirmationDialog
          isEditMode={isEditMode}
          isSaving={isSaving}
          onConfirm={handleConfirmSave}
          onCancel={() => {
            if (!savingRef.current) setShowSaveConfirmation(false);
          }}
        />
      ) : null}

      {pendingAddressSuggestion ? (
        <AddressSuggestionDialog
          suggestion={pendingAddressSuggestion}
          currentAddress={formData.direccion.trim()}
          onAccept={handleAcceptSuggestedAddress}
          onDismiss={() => setPendingAddressSuggestion(null)}
        />
      ) : null}

      {createdCaseSummary && showCreatedCaseSummary ? (
        <>
          <div
            className="modal show d-block case-created-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="casoCreadoTitulo"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setShowCreatedCaseSummary(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <button
                  type="button"
                  className="case-created-modal__close"
                  aria-label="Cerrar resumen del caso guardado"
                  title="Cerrar sin salir del formulario"
                  onClick={() => setShowCreatedCaseSummary(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
                <div className="modal-header">
                  <h4 className="modal-title" id="casoCreadoTitulo">Caso agregado correctamente</h4>
                </div>
                <div className="modal-body">
                  <div className="alert alert-success mb-3" role="status">
                    Número de caso: <strong>{createdCaseSummary.protocoloGeneral}</strong>
                  </div>
                  <dl className="row mb-0">
                    <dt className="col-sm-5">Fecha del caso</dt><dd className="col-sm-7">{createdCaseSummary.fechaCaso}</dd>
                    <dt className="col-sm-5">Propietario</dt><dd className="col-sm-7">{createdCaseSummary.propietario}</dd>
                    <dt className="col-sm-5">Tipo de estudio</dt><dd className="col-sm-7">{createdCaseSummary.tipoEstudio}</dd>
                    <dt className="col-sm-5">Ubicación</dt><dd className="col-sm-7">{createdCaseSummary.ubicacion}</dd>
                    <dt className="col-sm-5">Animales totales</dt><dd className="col-sm-7">{createdCaseSummary.animalesTotales}</dd>
                    <dt className="col-sm-5">Enfermos / muertos</dt><dd className="col-sm-7">{createdCaseSummary.animalesEnfermos} / {createdCaseSummary.animalesMuertos}</dd>
                  </dl>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-brand" onClick={continueAfterCaseCreation} autoFocus>
                    Continuar a agregar muestra
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show case-created-modal-backdrop" />
        </>
      ) : null}

      <ToastContainer />
    </div>
  );
};

export default NuevoCaso;
